import React, { useEffect, useState } from 'react';
import { useNavigation } from 'context/NavigationContext';
import {
  BringzzPageContainer,
  BringzzButton,
  BringzzIcon,
  BringzzText,
  BringzzInput,
  BringzzBottomDrawer,
  BringzzCollectionItem,
  BringzzRadioList,
  BringzzRadio,
  BringzzModal,
  BringzzTabs,
  BringzzTab,
  BringzzTabPanel,
  BringzzImage
} from '@bringzz/components';
import useRequest from 'hooks/useRequest';
import { useAuth } from 'context/AuthContext';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import AvatarExample from 'assets/images/AvatarExample.png';
import DefaultProductImage from 'assets/DefaultProductImage.png';
import LetterAvatar from '../Pharmacy/Profile/components/LetterAvatar';

let state = {
  checked: <div></div>,
  unChecked: <div></div>
};

const BookmarksView = () => {
  const { t } = useTranslation();
  const { backPress, navigate } = useNavigation();
  const { sendRequest, loading } = useRequest();
  const [selectedCollection, setSelectedCollection] = useState('');
  const [openDrawer, setOpenDrawer] = useState(false);
  const [addCollectionOpen, setAddCollectionOpen] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [bottomDrawerType, setBottomDrawerType] = useState(null);
  const [collections, setCollections] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [postBookmarks, setPostBookmarks] = useState([]);
  const [productBookmarks, setProductBookmarks] = useState([]);
  const [pharmacyBookmarks, setPharmacyBookmarks] = useState([]);
  const [collectionUpdate, setCollectionUpdate] = useState(false);
  const { user } = useAuth();

  const fetchCollections = async () => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/bookmarkcollection/getAllCollections`,
        data: { userId: user.id }
      });
      if (response.status === 'success') {
        setCollections(response.data);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load collections');
    }
  };

  const fetchBookmarks = async () => {
    try {
      const response = await sendRequest({
        method: 'GET',
        url: `/module/bookmark/getAllBookmarks`,
      });
      if (response.status === 'success') {
        const allBookmarks = response.data || [];
        setBookmarks(allBookmarks);
        
        // Separate bookmarks by type (include all bookmarks regardless of collection)
        const posts = allBookmarks.filter(bookmark => bookmark.type == 0);
        const products = allBookmarks.filter(bookmark => bookmark.type == 1);
        const pharmacies = allBookmarks.filter(bookmark => bookmark.type == 2);
        
        setPostBookmarks(posts);
        setProductBookmarks(products);
        setPharmacyBookmarks(pharmacies);
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to load bookmarks');
    }
  };

  useEffect(() => {
    fetchCollections();
    fetchBookmarks();
  }, [collectionUpdate]);

  useEffect(() => {
    const handleFocus = () => {
      fetchBookmarks();
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  return (
    <BringzzPageContainer
      header={{
        left: (
          <BringzzIcon
            size='22'
            icon='IconChevronLeft'
            folder='LineIcons'
            className='cursor-pointer'
            onClick={backPress}
          />
        ),
        title: <BringzzText tag='h2'>{t('component.bookmarks.title')}</BringzzText>
      }}
      footer={
        <div className='pb-28 px-6'>
          <BringzzButton
            size='lg'
            className='bg-magic-lilac w-full flex justify-center text-midnight-blue'
            onClick={() => { setBottomDrawerType(null); setAddCollectionOpen(true); }}
            disabled={loading}
          >
            {t('component.bookmarks.collections.create')}
          </BringzzButton>
        </div>
      }
    >
      {loading ? (
        <div className="flex items-center justify-center p-8">
          <BringzzIcon
            size="32"
            icon="IconSpinner"
            folder="LineIcons"
            className="animate-spin text-magic-lilac"
          />
        </div>
      ) : (
        <div className='space-y-8 p-6'>
          {/* Collections Section */}
          <div>
            <BringzzText tag='h3' className='text-xl font-bold mb-4'>
              {t('component.bookmarks.collections.title')}
            </BringzzText>
            {collections.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm">
                <BringzzIcon
                  size="48"
                  icon="IconFolder"
                  folder="LineIcons"
                  className="text-gray-400 mb-4"
                />
                <BringzzText tag="h3" className="text-gray-600 mb-2">
                  {t('component.bookmarks.collections.empty.title')}
                </BringzzText>
                <BringzzText className="text-gray-500">
                  {t('component.bookmarks.collections.empty.description')}
                </BringzzText>
              </div>
            ) : (
              <div className='grid grid-cols-1 gap-4'>
                {collections.map((collection) => {
                  const collectionBookmarkCount = bookmarks.filter(bookmark => 
                    bookmark.collectionUUID === collection.integrationId
                  ).length;
                  return (
                    <BringzzCollectionItem
                      key={collection.id}
                      onClick={() => navigate(`/settings/bookmarks/collection?id=${collection.integrationId}`)}
                      collection={collection}
                      items={collectionBookmarkCount}
                      setOpenDrawer={setOpenDrawer}
                      setSelectedCollection={setSelectedCollection}
                      className='hover:shadow-lg transition-shadow duration-200'
                    />
                  );
                })}
              </div>
            )}
          </div>

          <div>
            <BringzzText tag='h3' className='text-xl font-bold mb-4'>
              {t('component.bookmarks.saved_items.title')}
            </BringzzText>
            
            <BringzzTabs defaultActiveTab="posts">
              {/* Posts Tab */}
              <BringzzTab id="posts">
                <div className="flex items-center">
                  <BringzzIcon icon="IconLayoutAlt" folder="LineIcons" size="20" className="mr-2" />
                  <BringzzText>{t('component.bookmarks.tabs.posts')} ({postBookmarks.length})</BringzzText>
                </div>
              </BringzzTab>
              <BringzzTabPanel id="posts">
                {postBookmarks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm">
                    <BringzzIcon
                      size="48"
                      icon="IconLayoutAlt"
                      folder="LineIcons"
                      className="text-gray-400 mb-4"
                    />
                    <BringzzText tag="h3" className="text-gray-600 mb-2">
                      {t('component.bookmarks.empty_states.no_saved_posts')}
                    </BringzzText>
                    <BringzzText className="text-gray-500">
                      {t('component.bookmarks.empty_states.posts_bookmark_description')}
                    </BringzzText>
                  </div>
                ) : (
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {postBookmarks.map((bookmark) => (
                      <div
                        key={bookmark.id}
                        className='bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300'
                      >
                        <div className='p-4 relative'>
                          <BringzzIcon
                            icon="IconBookmarkSolid"
                            folder="HeroIcons"
                            size="20"
                            className="absolute top-2 right-2 text-purple-500 cursor-pointer hover:text-red-600 transition-colors"
                            onClick={async () => {
                              try {
                                const response = await sendRequest({
                                  method: 'POST',
                                  url: '/module/bookmark/removeBookmark',
                                  data: { elementUUID: bookmark.elementUUID, type: 0 }
                                });
                                if (response.status === 'success') {
                                  setCollectionUpdate(prev => !prev);
                                  toast.success(t('component.bookmarks.messages.post_bookmark_removed'));
                                }
                              } catch (error) {
                                toast.error(t('component.bookmarks.messages.failed_remove_bookmark'));
                              }
                            }}
                          />
                          {bookmark.post_details && bookmark.post_details.length > 0 ? (
                            <>
                              <BringzzText tag='h4' className='text-lg font-semibold mb-2'>
                                {bookmark.post_details[0].headline}
                              </BringzzText>
                              <BringzzText tag='p' className='text-gray-600 mb-4 line-clamp-3'>
                                {bookmark.post_details[0].description}
                              </BringzzText>
                            </>
                          ) : (
                            <BringzzText tag='h4' className='text-lg font-semibold mb-2'>
                              {t('component.bookmarks.labels.saved_post')}
                            </BringzzText>
                          )}

                        </div>
                        <div className='border-t px-4 py-3 bg-gray-50'>
                          <BringzzButton
                            size='sm'
                            className='w-full flex items-center justify-center'
                            onClick={() => navigate(`/post/details/${bookmark.elementUUID}`)}
                          >
                            <BringzzIcon
                              icon='IconExternalLink'
                              folder='LineIcons'
                              className='mr-2'
                              size='16'
                            />
                            {t('component.bookmarks.actions.view_post')}
                          </BringzzButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </BringzzTabPanel>
              
              {/* Products Tab */}
              <BringzzTab id="products">
                <div className="flex items-center">
                  <BringzzIcon icon="IconSearch" folder="LineIcons" size="20" className="mr-2" />
                  <BringzzText>{t('component.bookmarks.tabs.products')} ({productBookmarks.length})</BringzzText>
                </div>
              </BringzzTab>
              <BringzzTabPanel id="products">
                {productBookmarks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm">
                    <BringzzIcon
                      size="48"
                      icon="IconSearch"
                      folder="LineIcons"
                      className="text-gray-400 mb-4"
                    />
                    <BringzzText tag="h3" className="text-gray-600 mb-2">
                      {t('component.bookmarks.empty_states.no_saved_products')}
                    </BringzzText>
                    <BringzzText className="text-gray-500">
                      {t('component.bookmarks.empty_states.products_bookmark_description')}
                    </BringzzText>
                  </div>
                ) : (
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                    {productBookmarks.map((bookmark) => (
                      <div
                        key={bookmark.id}
                        className='bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300'
                      >
                        <div className='p-4 relative'>
                          <BringzzIcon
                            icon="IconBookmarkSolid"
                            folder="HeroIcons"
                            size="20"
                            className="absolute top-2 right-2 text-purple-500 cursor-pointer hover:text-red-600 transition-colors z-10"
                            onClick={async () => {
                              try {
                                const response = await sendRequest({
                                  method: 'POST',
                                  url: '/module/bookmark/removeBookmark',
                                  data: { elementUUID: bookmark.elementUUID, type: 1 }
                                });
                                if (response.status === 'success') {
                                  setCollectionUpdate(prev => !prev);
                                  toast.success(t('component.bookmarks.messages.product_bookmark_removed'));
                                }
                              } catch (error) {
                                toast.error(t('component.bookmarks.messages.failed_remove_bookmark'));
                              }
                            }}
                          />
                          {bookmark.productInfo ? (
                            <>
                              <img 
                                src={bookmark.productInfo.productImage || DefaultProductImage} 
                                alt={bookmark.productInfo.name}
                                className={`w-full h-32 mb-3 rounded ${
                                  bookmark.productInfo.productImage 
                                    ? 'object-cover' 
                                    : 'object-contain p-4'
                                }`}
                                onError={(e) => {
                                  e.target.src = DefaultProductImage;
                                  e.target.className = e.target.className.replace('object-cover', 'object-contain p-4');
                                }}
                              />
                              <BringzzText tag='h4' className='text-sm font-semibold mb-2 line-clamp-2'>
                                {bookmark.productInfo.name || 'Product'}
                              </BringzzText>
                              <BringzzText tag='p' className='text-green-600 font-bold mb-2'>
                                €{bookmark.productInfo.price || '0.00'}
                              </BringzzText>
                            </>
                          ) : (
                            <BringzzText tag='h4' className='text-lg font-semibold mb-2'>
                              {t('component.bookmarks.labels.saved_product')}
                            </BringzzText>
                          )}
                        </div>
                        <div className='border-t px-4 py-3 bg-gray-50'>
                          <BringzzButton
                            size='sm'
                            className='w-full flex items-center justify-center'
                            onClick={() => navigate(`/product/details/${bookmark.productInfo?.integrationId || bookmark.elementUUID}`)}
                          >
                            <BringzzIcon
                              icon='IconExternalLink'
                              folder='LineIcons'
                              className='mr-2'
                              size='16'
                            />
                            {t('component.bookmarks.actions.view_product')}
                          </BringzzButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </BringzzTabPanel>
              
              {/* Pharmacies Tab */}
              <BringzzTab id="pharmacies">
                <div className="flex items-center">
                  <BringzzIcon icon="IconPharmacy" folder="HealthIcons" size="20" className="mr-2" />
                  <BringzzText>{t('component.bookmarks.tabs.pharmacies')} ({pharmacyBookmarks.length})</BringzzText>
                </div>
              </BringzzTab>
              <BringzzTabPanel id="pharmacies">
                {pharmacyBookmarks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center p-8 text-center bg-white rounded-xl shadow-sm">
                    <BringzzIcon
                      size="48"
                      icon="IconMedicalCross"
                      folder="LineIcons"
                      className="text-gray-400 mb-4"
                    />
                    <BringzzText tag="h3" className="text-gray-600 mb-2">
                      {t('component.bookmarks.empty_states.no_saved_pharmacies')}
                    </BringzzText>
                    <BringzzText className="text-gray-500">
                      {t('component.bookmarks.empty_states.pharmacies_bookmark_description')}
                    </BringzzText>
                  </div>
                ) : (
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {pharmacyBookmarks.map((bookmark) => (
                      <div
                        key={bookmark.id}
                        className='bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow duration-300'
                      >
                        <div className='p-4 relative'>
                          <BringzzIcon
                            icon="IconBookmarkSolid"
                            folder="HeroIcons"
                            size="20"
                            className="absolute top-2 right-2 text-purple-500 cursor-pointer hover:text-red-600 transition-colors"
                            onClick={async () => {
                              try {
                                const response = await sendRequest({
                                  method: 'POST',
                                  url: '/module/bookmark/removeBookmark',
                                  data: { elementUUID: bookmark.elementUUID, type: 2 }
                                });
                                if (response.status === 'success') {
                                  setCollectionUpdate(prev => !prev);
                                  toast.success(t('component.bookmarks.messages.pharmacy_bookmark_removed'));
                                }
                              } catch (error) {
                                toast.error(t('component.bookmarks.messages.failed_remove_bookmark'));
                              }
                            }}
                          />
                          <div className="flex items-center mb-3">
                            {bookmark.pharmacy_details?.pharmacyPic ? (
                              <img 
                                src={bookmark.pharmacy_details.pharmacyPic} 
                                alt={bookmark.pharmacy_details.name}
                                className="w-12 h-12 rounded-full object-cover mr-3"
                              />
                            ) : (
                              <LetterAvatar 
                                name={bookmark.pharmacy_details?.name || 'Pharmacy'} 
                                size="w-12 h-12"
                                fontSize="text-sm"
                                className="mr-3"
                              />
                            )}
                            <div>
                              <BringzzText tag='h4' className='text-lg font-semibold'>
                                {bookmark.pharmacy_details?.name || t('component.bookmarks.labels.pharmacy')}
                              </BringzzText>
                              <BringzzText className='text-gray-500 text-sm'>
                                Saved {bookmark.created || t('component.bookmarks.labels.saved_recently')}
                              </BringzzText>
                            </div>
                          </div>

                        </div>
                        <div className='border-t px-4 py-3 bg-gray-50'>
                          <BringzzButton
                            size='sm'
                            className='w-full flex items-center justify-center'
                            onClick={() => navigate(`/pharmacy/profile/${bookmark.elementUUID}`)}
                          >
                            <BringzzIcon
                              icon='IconExternalLink'
                              folder='LineIcons'
                              className='mr-2'
                              size='16'
                            />
                            {t('component.bookmarks.actions.view_pharmacy')}
                          </BringzzButton>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </BringzzTabPanel>
            </BringzzTabs>
          </div>
        </div>
      )}

      {/* Bottom Drawers */}
      <BringzzBottomDrawer
        isOpen={openDrawer}
        close={() => setOpenDrawer(false)}
        title={t('component.bookmarks.collections.manage')}
      >
        <div className='px-6 py-6'>
          <BringzzRadioList state={state} className='rounded-xl w-full text-center'>
            <BringzzRadio
              value='Male'
              className='p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400 flex'
            >
              <div
                onClick={() => {
                  setBottomDrawerType('rename');
                  setOpenDrawer(false);
                  setAddCollectionOpen(true);
                }}
                className='w-full text-start'
              >
                {t('component.bookmarks.collections.rename.action')}
              </div>
            </BringzzRadio>
            <BringzzRadio
              value='Female'
              className='p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400'
            >
              <div
                onClick={() => {
                  setOpenDrawer(false);
                  setModalOpen(true);
                }}
                className='w-full text-start text-bold'
              >
                {t('component.bookmarks.collections.delete.action')}
              </div>
            </BringzzRadio>
            <BringzzRadio
              className='p-2 py-4 hover:bg-slate- border-1 border-t border-gray-400'
              value='Diverse'
            >
              <div 
                className='w-full text-start'
                onClick={async () => {
                  console.log('Share clicked!');
                  console.log('Selected collection:', selectedCollection);
                  const collectionUrl = `${window.location.origin}/settings/bookmarks/collection?id=${selectedCollection.integrationId}`;
                  console.log('Collection URL:', collectionUrl);
                  navigator.clipboard.writeText(collectionUrl);
                  toast.success('Collection link copied!');
                  setOpenDrawer(false);
                }}
              >
                {t('component.bookmarks.collections.share')}
              </div>
            </BringzzRadio>
          </BringzzRadioList>
        </div>
      </BringzzBottomDrawer>

      <BringzzBottomDrawer
        isOpen={addCollectionOpen}
        close={() => setAddCollectionOpen(false)}
        title={bottomDrawerType === 'rename' 
          ? t('component.bookmarks.collections.rename.title')
          : t('component.bookmarks.collections.new.title')}
      >
        <div className='px-6 py-6'>
          <BringzzInput
            label={t('component.bookmarks.collections.input.label')}
            placeholder={t('component.bookmarks.collections.input.placeholder')}
            defaultValue={selectedCollection.name}
            containerClassname='w-full'
            onChange={(e, isValid, newValue) => {
              setNewCollectionName(newValue);
            }}
          />
        </div>
        <div className='text-center mb-12 px-4'>
          <BringzzButton
            className='bg-magic-lilac w-full flex justify-center'
            size='lg'
            onClick={async () => {
              try {
                if (bottomDrawerType === 'rename') {
                  const response = await sendRequest({
                    method: 'POST',
                    url: `/module/bookmarkcollection/updateCollection`,
                    data: {
                      collectionId: selectedCollection.id,
                      name: newCollectionName
                    }
                  });
                  if (response.status === 'success') {
                    setAddCollectionOpen(false);
                    setCollectionUpdate(prev => !prev);
                    toast.success('Collection renamed successfully');
                  }
                } else {
                  const response = await sendRequest({
                    method: 'POST',
                    url: `/module/bookmarkcollection/createCollection`,
                    data: { name: newCollectionName, icon: "an_icon", note: "A note?" }
                  });
                  if (response.status === 'success') {
                    setAddCollectionOpen(false);
                    setCollectionUpdate(prev => !prev);
                    toast.success('Collection created successfully');
                  }
                }
              } catch (error) {
                console.error(error);
                toast.error(bottomDrawerType === 'rename' ? 'Failed to rename collection' : 'Failed to create collection');
              }
            }}
            disabled={loading || !newCollectionName}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <BringzzIcon
                  size="20"
                  icon="IconSpinner"
                  folder="LineIcons"
                  className="animate-spin mr-2"
                />
                {bottomDrawerType === 'rename' 
                  ? t('component.bookmarks.collections.rename.saving')
                  : t('component.bookmarks.collections.new.creating')}
              </div>
            ) : (
              bottomDrawerType === 'rename'
                ? t('component.bookmarks.collections.rename.action')
                : t('component.bookmarks.collections.new.action')
            )}
          </BringzzButton>
        </div>
      </BringzzBottomDrawer>

      {modalOpen && (
        <BringzzModal
          title={t('component.bookmarks.collections.delete.title')}
          description={t('component.bookmarks.collections.delete.description', { name: selectedCollection.name })}
          onCancel={() => setModalOpen(false)}
          onConfirm={async () => {
            try {
              const response = await sendRequest({
                method: 'DELETE',
                url: `/module/bookmarkcollection/deleteCollection`,
                data: {
                  collectionId: selectedCollection.id
                }
              });
              if (response.status === 'success') {
                setModalOpen(false);
                setCollectionUpdate(prev => !prev);
                toast.success('Collection deleted successfully');
              }
            } catch (error) {
              console.error(error);
              toast.error('Failed to delete collection');
            }
          }}
          confirmButtonText={t('component.bookmarks.collections.delete.action')}
        />
      )}
    </BringzzPageContainer>
  );
};

export default BookmarksView;